export interface AllFoodsList {
  total: number;
  food: FoodMeta[];
}

export interface FoodMeta {
  id: number;
  image_url: string;
  is_saved: boolean;
  rank: number;
  title: string;
}

export type GetAllFoodsParam = Partial<{
  [key: number]: string[];
}>;

export interface GetAllSupplementParam {
  filter_id?: string[];
}
export type GetAllFoodsResponse = FoodMeta[];

export interface SaveFoodData {
  food_id: number;
  flag: IBookmarkFlag;
  key?: GetAllFoodsParam; // It is used only for optimistic update at client end.
}

export interface FoodDetailParam {
  food_id: number;
}
export interface SupplementDetailParam {
  ingredient_id: number;
}

export interface SupplementDetail {
  description: string;
  related_researches: string[];
  scores: Score[];
  title: string;
  dosage_with_unit: string;
  group_name: string;
  id: number;
  badges: Badge[];
  is_bookmarked: boolean;
  product_id: number[];
}

export interface Score {
  id: number;
  name: string;
  rank: string;
  score: string;
  rank_color: string;
  score_type: 'l3' | 'l4';
}

export const BookmarkFlag = {
  Add: 'ADD',
  Remove: 'REMOVE',
} as const;

export type IBookmarkFlag = (typeof BookmarkFlag)[keyof typeof BookmarkFlag];

export interface FoodRank {
  color: string;
  description: string;
  id: number;
  image_url: string[];
  ordinal: number;
  title: string;
}

export type GetFoodRankResponse = FoodRank[];

export interface NutritionTips {
  based_on: string;
  nutrition_tips: string;
}

export type NutritionTipsResponse = {
  nutrition_tips: NutritionTips[];
};
export interface SupplementListData {
  supplement: Supplement[];
}

export interface Supplement {
  formula: Formula;
  product_id: number;
  product_name: string;
  sub_product: string | null;
}

export type ISupplementListResponse = Supplement[];

export interface Formula {
  blends: Blend[];
  ingredients: Ingredient[];
}

export interface Blend {
  ingredients: Ingredient[];
  name: string;
  volume: string;
}

export interface Ingredient {
  id: number;
  is_bookmarked: boolean;
  name: string;
  volume?: string;
}

export interface DailyServingData {
  macros: string;
  servings: number;
}

export type IDailyServingDataResponse = DailyServingData[];

export interface BookmarkSupplementData {
  product_id: number;
  ingredient_id: number;
  flag: IBookmarkFlag;
}

export type ISaveSupplementResponse = null;

export interface FoodDetailData {
  about: About;
  badges: Badge[];
  whyThisFood: FoodHealthDataSubcategory[];
  basic_details: FoodBasicDetails;
}

export interface FoodBasicDetails {
  color: string;
  image_url: string;
  name: string;
  rank: string;
  servings: string;
  is_saved: boolean;
}

export interface FoodDetailMeta {
  meta_data: FoodDetailMetaDetail[];
}

export type IFoodMetaDetailResponse = FoodDetailMeta;

export interface FoodDetailMetaDetail {
  description: string;
  links: string[];
  title: string;
}

export interface FoodHealthData {
  category: string;
  subcategory: FoodHealthDataSubcategory[];
}

export interface FoodHealthDataSubcategory {
  description: string;
  id: number;
  link: string[];
  rank: string;
  rank_color: string;
  title: string;
  score: number;
  score_type: 'l3' | 'l4';
}

export interface About {
  about_this_food: string;
  food_composition: string;
  how_to_consume: string;
  macronutrients: Macronutrients[];
}

export interface Macronutrients {
  title: string;
  value: string;
}

export interface Badge {
  description: string;
  image_url: string;
  title: string;
}

export interface BookmarkFoodMutationContext {
  previousFoods: {
    total: number;
    food: FoodMeta[];
  };
}

export type IFilterResponse = FoodFilterResult[];
export type ISupplementResponse = FoodFilterResult;

export interface FoodFilterResult {
  filter: FoodFilter[];
}

export type IFoodProductResponse = FoodProduct[];

export interface FoodProduct {
  id: number;
  image_url: string;
  ingredients_count?: number;
  is_subscribed: boolean;
  name: string;
}
export interface FoodProductQueryParam {
  with_subscription_status?: boolean;
  subscribed_only?: boolean;
}
export interface FoodFilter {
  title: string;
  subgroup: string[];
  id: number;
}

export type ISupplementFilterResponse = FoodFilterResult;
